import GlassCard from '../ui/glass/components/GlassCard';

import React, { useState, useEffect } from 'react';
import { db, functions } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Box, Typography, TextField, Button, Grid, Divider, Alert } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import PageHeader from '../components/common/PageHeader';
import { Loading } from '../components/common/Loading';

const FirmaBilgileri = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [form, setForm] = useState({
        firmaAdi: '',
        altBaslik1: '',
        altBaslik2: '',
        adres: '',
        telefon: '',
        web: '',
        logoUrl: '',
        bankaAdi: '',
        iban: '',
        aliciAdi: '',
        servisNotu1: 'Azami tamir süresi 20 iş günüdür.',
        servisNotu2: '90 gün içerisinde teslim alınmayan cihazlardan firmamız mesul değildir.',
        servisNotu3: 'Müşteri, cihazını teslim ederken formda belirtilen arızaları ve şartları kabul etmiş sayılır.'
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, "settings", "company_info");
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setForm(prev => ({ ...prev, ...docSnap.data() }));
            }
        } catch (error) {
            console.error("Ayarlar yüklenirken hata:", error);
            setMessage({ type: 'error', text: 'Ayarlar yüklenemedi.' });
        } finally {
            setLoading(false);
        }
    };

    const formatIBAN = (value) => {
        let raw = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        const chunks = [];
        for (let i = 0; i < raw.length; i += 4) {
            chunks.push(raw.slice(i, i + 4));
        }
        return chunks.join(' ');
    };

    const validateIBAN = (iban) => {
        const cleanIBAN = iban.replace(/\s/g, '');
        const trIbanRegex = /^TR[0-9]{24}$/;
        return trIbanRegex.test(cleanIBAN);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'iban') {
            setForm(prev => ({ ...prev, [name]: formatIBAN(value) }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        if (form.iban && !validateIBAN(form.iban)) {
            setMessage({ type: 'error', text: 'Geçersiz IBAN formatı! (TR ile başlamalı ve 26 karakter olmalı)' });
            return;
        }

        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await setDoc(doc(db, "settings", "company_info"), form);
            setMessage({ type: 'success', text: 'Ayarlar başarıyla kaydedildi.' });
        } catch (error) {
            console.error("Kaydederken hata:", error);
            setMessage({ type: 'error', text: 'Kaydedilirken bir hata oluştu.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loading message="Firma bilgileri yükleniyor..." />;

    return (
        <Box sx={{ p: { xs: 1, md: 1 }, maxWidth: 'md', m: 'auto' }}>
            <PageHeader
                title="Firma Bilgileri & Ayarlar"
                subtitle="Servis formlarında ve çıktılarda görünecek firma bilgilerinizi buradan düzenleyebilirsiniz."
                icon={<BusinessIcon />}
            />

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
                    {message.text}
                </Alert>
            )}

            <GlassCard sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Genel Bilgiler</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Firma Adı (Başlık)" name="firmaAdi" value={form.firmaAdi} onChange={handleChange} placeholder="Örn: RESLIFE MEDİKAL" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Logo URL (Resim Adresi)" name="logoUrl" value={form.logoUrl} onChange={handleChange} placeholder="/reslife-logo.png veya https://..." />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Alt Başlık 1" name="altBaslik1" value={form.altBaslik1} onChange={handleChange} placeholder="Örn: Uyku ve Solunum Cihazları" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Alt Başlık 2" name="altBaslik2" value={form.altBaslik2} onChange={handleChange} placeholder="Örn: TIBBI CİHAZLAR LTD.ŞTİ" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Adres" name="adres" value={form.adres} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Telefon" name="telefon" value={form.telefon} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Web Sitesi" name="web" value={form.web} onChange={handleChange} />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>Banka Bilgileri</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Banka Adı" name="bankaAdi" value={form.bankaAdi} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth label="Alıcı Adı" name="aliciAdi" value={form.aliciAdi} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="IBAN"
                            name="iban"
                            value={form.iban}
                            onChange={handleChange}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                            inputProps={{ maxLength: 32 }} // TR + 24 digits + spaces
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>Servis Form Notu</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Bu not, yazdırılan servis formunun alt kısmında görünecektir.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Servis Form Notu" name="servisNotu1" value={form.servisNotu1} onChange={handleChange} multiline rows={3} />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="error"
                        size="large"
                        onClick={async () => {
                            if (window.confirm("DİKKAT! Bu işlem veritabanındaki 'sahipsiz' (Müşterisi silinmiş ama kendisi duran) tüm verileri temizleyecektir. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?")) {
                                setSaving(true);
                                try {
                                    const cleanDb = httpsCallable(functions, 'cleanDatabase');
                                    const result = await cleanDb();
                                    console.log(result.data);
                                    alert(`Temizlik Tamamlandı.\nSilinen Kayıtlar:\n${JSON.stringify(result.data.deletedCounts, null, 2)}`);
                                } catch (error) {
                                    console.error(error);
                                    alert("Hata: " + error.message);
                                } finally {
                                    setSaving(false);
                                }
                            }
                        }}
                        disabled={saving}
                    >
                        Veri Tabanı Temizliği (Admin)
                    </Button>

                    <Button variant="contained" size="large" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </Box>
            </GlassCard>
        </Box>
    );
};

export default FirmaBilgileri;
